// composables/usePedidoItemsEdit.js
import { ref, computed } from "vue"
import Swal from "sweetalert2"
import {
  getPedidoItems,
  crearPedidoItem,
  actualizarPedidoItem,
  eliminarPedidoItem,
  actualizarPedido,
} from "@/utils/api_backend"

export function usePedidoItemsEdit(catArticulos) {

  const pedidoActual       = ref(null)
  const items              = ref([])
  const itemNuevo          = ref({ articulo_id: "", cantidad: null, notas: "" })
  const guardando          = ref(false)
  const cargando           = ref(false)

  let _snapshot = []

  // ── Computeds ────────────────────────────────────────────
  const totalPiezas        = computed(() => items.value.reduce((s, i) => s + (Number(i.cantidad) || 0), 0))
  const itemsConMatch      = computed(() => items.value.filter((i) =>  i.articulo_id).length)
  const itemsSinMatch      = computed(() => items.value.filter((i) => !i.articulo_id).length)

  // ── Helpers catálogo ─────────────────────────────────────
  function codigoArticulo(id) {
    return catArticulos.value.find((a) => a.id === id)?.codigo || "—"
  }
  function descripcionArticulo(id) {
    const a = catArticulos.value.find((a) => a.id === id)
    return a?.descripcion || a?.codigo || "—"
  }
  function filtrarArticulos(texto) {
    const q = (texto || "").toLowerCase()
    return catArticulos.value
      .filter((a) => !q || a.codigo.toLowerCase().includes(q) || (a.descripcion || "").toLowerCase().includes(q))
      .slice(0, 5)
  }
  function asignarArticuloItem(item, a) {
    item.articulo_id = a.id
    item._busqueda   = ""
    item._abierto    = false
  }

  // ── Cargar ítems existentes ───────────────────────────────
  async function cargarItems(pedido) {
    pedidoActual.value = pedido
    items.value        = []
    _snapshot          = []
    itemNuevo.value    = { articulo_id: "", cantidad: null, notas: "" }

    cargando.value = true
    try {
      const raw = await getPedidoItems(pedido.numero_pedido)
      items.value = raw.map((r) => {
        const art = catArticulos.value.find((a) => a.codigo === r.articulo)
        return {
          _itemId:     r.id,
          articulo_id: art?.id || null,
          cantidad:    r.cantidad,
          notas:       r.notas || "",
          _desdeIa:    false,
          _busqueda:   r.articulo,
          _abierto:    false,
          confianza:   100,
        }
      })
      _snapshot = items.value.map((i) => ({ ...i }))
    } catch (e) {
      console.error("Error cargando ítems para edición:", e)
      Swal.fire("Error", "No se pudieron cargar los ítems del pedido.", "error")
    } finally {
      cargando.value = false
    }
  }

  // ── Agregar ítem manual ───────────────────────────────────
  function agregarItem() {
    if (!itemNuevo.value.articulo_id || !itemNuevo.value.cantidad) return
    const existe = items.value.find((i) => i.articulo_id === itemNuevo.value.articulo_id)
    if (existe) {
      existe.cantidad += itemNuevo.value.cantidad
      if (itemNuevo.value.notas)
        existe.notas = existe.notas ? `${existe.notas} | ${itemNuevo.value.notas}` : itemNuevo.value.notas
    } else {
      items.value.push({
        _itemId:     null,
        articulo_id: itemNuevo.value.articulo_id,
        cantidad:    itemNuevo.value.cantidad,
        notas:       itemNuevo.value.notas || "",
        _desdeIa:    false,
        _busqueda:   codigoArticulo(itemNuevo.value.articulo_id),
        _abierto:    false,
        confianza:   100,
      })
    }
    itemNuevo.value = { articulo_id: "", cantidad: null, notas: "" }
  }

  function quitarItem(idx) { items.value.splice(idx, 1) }

  // ── Guardar cambios ───────────────────────────────────────
  // NOTA: el parámetro 'guia' fue eliminado — las guías se gestionan
  // en tiempo real desde el modal con agregarGuiaPedido / eliminarGuiaPedido.
  async function guardarCambios(prioridad = "", onExito, notas = null) {
    if (!pedidoActual.value) return
    guardando.value = true

    try {
      const promesas = []

      // PATCH pedido: prioridad y/o notas si cambiaron
      const prioridadActual = pedidoActual.value.prioridad || ""
      const notasActual     = pedidoActual.value.notas     ?? ""
      const patchPedido     = {}

      if ((prioridad || "") !== prioridadActual)
        patchPedido.prioridad = prioridad || null

      if (notas !== null && notas !== notasActual)
        patchPedido.notas = notas

      if (Object.keys(patchPedido).length > 0)
        promesas.push(actualizarPedido(pedidoActual.value.numero_pedido, patchPedido))

      // Diff de ítems
      for (const item of items.value) {
        if (!item.articulo_id) continue

        if (!item._itemId) {
          promesas.push(
            crearPedidoItem({
              pedido_id:   pedidoActual.value.numero_pedido,
              articulo_id: item.articulo_id,
              cantidad:    item.cantidad,
              notas:       item.notas || "",
            })
          )
        } else {
          const original = _snapshot.find((s) => s._itemId === item._itemId)
          if (original) {
            const cambio = item.cantidad !== original.cantidad || item.notas !== original.notas
            if (cambio) {
              promesas.push(
                actualizarPedidoItem(item._itemId, {
                  cantidad: item.cantidad,
                  notas:    item.notas || "",
                })
              )
            }
          }
        }
      }

      // Eliminados
      for (const snap of _snapshot) {
        const sigueExistiendo = items.value.find((i) => i._itemId === snap._itemId)
        if (!sigueExistiendo) {
          promesas.push(eliminarPedidoItem(snap._itemId))
        }
      }

      await Promise.all(promesas)

      Swal.fire({
        icon: "success", title: "Pedido actualizado",
        text: `Pedido #${pedidoActual.value.numero_pedido} guardado correctamente`,
        timer: 2000, showConfirmButton: false,
      })
      onExito?.()
    } catch (err) {
      console.error(err)
      Swal.fire("Error", "No se pudieron guardar los cambios.", "error")
    } finally {
      guardando.value = false
    }
  }

  function reset() {
    pedidoActual.value      = null
    items.value             = []
    _snapshot               = []
    itemNuevo.value         = { articulo_id: "", cantidad: null, notas: "" }
  }

  return {
    pedidoActual, items, itemNuevo,
    guardando, cargando,
    totalPiezas, itemsConMatch, itemsSinMatch,
    codigoArticulo, descripcionArticulo, filtrarArticulos,
    asignarArticuloItem,
    agregarItem, quitarItem,
    cargarItems, guardarCambios, reset,
  }
}